"use client";

import React, { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, ShieldCheck, TrendingUp, Building, Clock, CircleCheck } from "lucide-react";
import { cn } from "@/lib/utils";
const steps = [{
  id: "source",
  num: "01",
  title: "Source",
  heading: "Identifying High-Yield Assets",
  description: "We leverage institutional data to identify and acquire asset-backed properties in high-demand areas, specifically suited for social housing requirements.",
  icon: Building,
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/yieldcapital_timeline_source.png",
  benefits: ["Data-driven acquisition", "Asset-backed security", "High-demand locations"]
}, {
  id: "lease",
  num: "02",
  title: "Lease",
  heading: "Securing Government-Backed Leases",
  description: "Properties are leased directly to government-backed housing associations on long-term Full Repairing and Insuring (FRI) contracts, ensuring complete security.",
  icon: ShieldCheck,
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/yieldcapital_timeline_lease.png",
  benefits: ["FRI lease agreements", "Government-backed partners", "Zero void periods"]
}, {
  id: "income",
  num: "03",
  title: "Income",
  heading: "Generating 10% Net Yield",
  description: "You begin receiving a secure, CPI-linked 10% net yield. Income is paid consistently, protected against inflation and market volatility.",
  icon: TrendingUp,
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/yieldcapital_timeline_income.png",
  benefits: ["10% net yield", "CPI-linked growth", "Consistent payouts"]
}, {
  id: "stability",
  num: "04",
  title: "Stability",
  heading: "10-Year Hands-Off Management",
  description: "Enjoy true passive income. The housing association handles all tenant management, maintenance, and repairs for the duration of the 10-year term.",
  icon: Clock,
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/yieldcapital_timeline_stability.png",
  benefits: ["Zero maintenance costs", "No tenant management", "10-year term security"]
}, {
  id: "exit",
  num: "05",
  title: "Exit",
  heading: "Clear & Profitable Exit Strategy",
  description: "At the end of the term, you have clear options: renew the lucrative lease, sell the asset on the open market, or liquidate through our network.",
  icon: CircleCheck,
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/yieldcapital_timeline_exit.png",
  benefits: ["Lease renewal options", "Capital appreciation", "Seamless liquidation"]
}];
export default function YourPathToPassiveIncome() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    const targetValue = (activeStepIndex + 1) / steps.length * 100;
    const timer = setTimeout(() => setProgressValue(targetValue), 50);
    return () => clearTimeout(timer);
  }, [activeStepIndex]);
  const activeStep = steps[activeStepIndex];
  return <section id="your-path-to-passive-income" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className={cn("text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tight">
            Your Path to <span className="text-primary">Passive Income</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            A transparent, five-step journey from initial investment to secure, long-term wealth generation through government-backed social housing.
          </p>
        </div>

        <div className={cn("flex flex-col gap-12 transition-all duration-1000 delay-200 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          {}
          <div className="relative">
            <div className="flex overflow-x-auto no-scrollbar justify-between items-end gap-8 pb-8 snap-x snap-mandatory">
              {steps.map((step, i) => <Button key={step.id} data-index={i} onClick={() => setActiveStepIndex(i)} variant="ghost" className="snap-start shrink-0 flex flex-col items-center gap-4 group relative w-24 md:w-32 h-auto p-0 focus:outline-none" aria-selected={activeStepIndex === i} role="tab">
                  <span className="text-6xl md:text-7xl font-default font-bold transition-all duration-500 ease-out select-none" style={{
                WebkitTextStroke: activeStepIndex === i ? "2px var(--primary)" : "2px var(--muted-foreground)",
                color: "transparent",
                opacity: activeStepIndex === i ? 1 : 0.3,
                transform: activeStepIndex === i ? "scale(1.1)" : "scale(1)"
              }}>
                    {step.num}
                  </span>
                  <span className={cn("text-sm md:text-base font-semibold tracking-wider uppercase transition-colors duration-300", activeStepIndex === i ? "text-primary" : "text-muted-foreground group-hover:text-foreground")}>
                    {step.title}
                  </span>
                  
                  {}
                  <div className={cn("absolute -bottom-10 w-3 h-3 rounded-full transition-all duration-300 z-10", activeStepIndex === i ? "bg-primary scale-100 shadow-[0_0_10px_var(--primary)]" : "bg-muted-foreground/30 scale-50")} />
                </Button>)}
            </div>
            
            {}
            <div className="absolute bottom-[22px] left-0 right-0 px-12 md:px-16 pointer-events-none">
               <Progress value={progressValue} className="h-1 bg-muted-foreground/20" />
            </div>
          </div>

          {}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center mt-8">
            {}
            <div className="flex flex-col gap-6 order-2 lg:order-1 min-h-[300px] justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary w-fit border border-primary/20">
                <activeStep.icon className="w-4 h-4" />
                <span className="text-sm font-semibold tracking-wide uppercase">Stage {activeStep.num}</span>
              </div>
              
              <h3 className="text-3xl md:text-4xl font-default font-bold text-foreground leading-tight">
                {activeStep.heading}
              </h3>
              
              <p className="text-lg text-muted-foreground leading-relaxed">
                {activeStep.description}
              </p>

              <ul className="flex flex-col gap-3 mt-2">
                {activeStep.benefits.map((benefit, idx) => <li key={idx} data-index={idx} className="flex items-center gap-3 text-foreground">
                    <CircleCheck className="w-5 h-5 text-primary shrink-0" />
                    <span>{benefit}</span>
                  </li>)}
              </ul>

              <div className="mt-6">
                <Button asChild size="lg" className="w-full sm:w-auto group">
                  <Link to="https://10percent-yield.com/contact/">
                    Get Investment Pack
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>

            {}
            <div className="order-1 lg:order-2 relative w-full aspect-[4/3] lg:aspect-square max-h-[500px] rounded-2xl overflow-hidden border border-border/50 shadow-2xl shadow-black/50 bg-muted/20">
              {steps.map((step, i) => <img key={step.id} data-index={i} src={step.img} alt={`Illustration for ${step.title} stage`} className={cn("absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out", activeStepIndex === i ? "opacity-100 scale-100" : "opacity-0 scale-105 pointer-events-none")} />)}
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-60" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}